/**
 * Cart-only overlay guard.
 *
 * The theme drawer component normally opens its <dialog> with showModal().
 * This guard also covers cart HTML replaced by Shopify's Section Rendering
 * API or a third-party add-to-cart script that only sets [open].
 */
(() => {
  const drawerSelector = 'theme-drawer#cart-drawer';
  const pageWrapperSelector = '.page-wrapper';

  function getCartDrawer() {
    return document.querySelector(drawerSelector);
  }

  function getPanel(drawer) {
    return drawer?.querySelector(':scope > dialog.theme-drawer__dialog') ?? null;
  }

  function panelIsModal(panel) {
    try {
      return panel.matches(':modal');
    } catch {
      return false;
    }
  }

  function promoteToModal(drawer) {
    const panel = getPanel(drawer);
    if (!drawer?.hasAttribute('open') || !panel || panelIsModal(panel)) return;

    try {
      if (panel.open) panel.close();
      panel.showModal();
    } catch {
      // The custom element may still be upgrading; the next observer pass
      // retries without interfering with the theme drawer lifecycle.
    }
  }

  function syncCartOverlay() {
    const drawer = getCartDrawer();
    const isOpen = Boolean(drawer?.hasAttribute('open'));

    document.documentElement.classList.toggle('cart-drawer-overlay-open', isOpen);

    if (isOpen) {
      document.querySelector(pageWrapperSelector)?.classList.remove('page-wrapper--drawer-open');
      promoteToModal(drawer);
    } else {
      const anotherDrawerIsOpen = Boolean(document.querySelector('theme-drawer[open]:not(#cart-drawer)'));
      document
        .querySelector(pageWrapperSelector)
        ?.classList.toggle(
          'page-wrapper--drawer-open',
          anotherDrawerIsOpen && window.matchMedia('(min-width: 990px)').matches
        );
    }
  }

  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target?.id !== 'cart-drawer') return;
    requestAnimationFrame(syncCartOverlay);
  });

  document.addEventListener('theme-drawer:close', (event) => {
    if (event.target?.id !== 'cart-drawer') return;
    requestAnimationFrame(syncCartOverlay);
  });

  const observer = new MutationObserver((records) => {
    if (
      records.some(
        (record) =>
          record.type === 'childList' ||
          (record.type === 'attributes' && record.target instanceof Element && record.target.matches(drawerSelector))
      )
    ) {
      syncCartOverlay();
    }
  });

  function init() {
    syncCartOverlay();
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['open'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
